import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Card, CardContent, Button, Badge, useToast,
	Tabs, TabsContent, TabsList, TabsTrigger,
	Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label,
	Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@hiring-intelligence/ui';
import { sourcingApi, SourcedCandidate } from '@/services/api';
import { OutreachPanel } from './OutreachPanel';
import { InterviewNotesPanel } from './InterviewNotesPanel';
import { CVGenerationPanel } from './CVGenerationPanel';
import { InterviewQuestionsPanel } from './InterviewQuestionsPanel';
import {
	Info, FileText, MessageSquare, Send, CheckCircle2,
	ChevronDown, ChevronUp, ExternalLink, Eye, Undo2, Ban, UserCheck, XCircle, UserX
} from 'lucide-react';

interface CandidatePipelineCardProps {
	candidate: SourcedCandidate;
	hiringRequestId: string;
	onOpenDetail: (candidate: SourcedCandidate) => void;
	onRefresh: () => void;
}

export type PipelineStep = 'info' | 'outreach' | 'cv' | 'interview' | 'recommend';

export const PIPELINE_STEPS: { key: PipelineStep; label: string; icon: React.ElementType }[] = [
	{ key: 'info', label: 'Info', icon: Info },
	{ key: 'outreach', label: 'Contact', icon: Send },
	{ key: 'interview', label: 'Notes', icon: MessageSquare },
	{ key: 'cv', label: 'CV', icon: FileText },
	{ key: 'recommend', label: 'Recommend', icon: CheckCircle2 },
];

export const REJECTED_BY_LABELS: Record<'us' | 'company' | 'candidate', string> = {
	us: 'Us',
	company: 'Company',
	candidate: 'Candidate'
};

export function getStepStatus(step: PipelineStep, candidate: CandidatePipelineCardProps['candidate']): 'done' | 'active' | 'pending' {
	switch (step) {
		case 'info':
			// Green: contactFetchedAt set (info + contact both done)
			if (candidate.contactFetchedAt) return 'done';
			// Blue: infoFetchedAt set but no contact yet
			if (candidate.infoFetchedAt) return 'active';
			// Gray: nothing fetched
			return 'pending';
		case 'outreach':
			// Green: has outreach history AND none pending
			if (candidate.outreachCount && candidate.outreachCount > 0 && !candidate.pendingOutreachCount) return 'done';
			// Blue: has outreach history but some still pending
			if (candidate.outreachCount && candidate.outreachCount > 0) return 'active';
			// Gray: no outreach history
			return 'pending';
		case 'cv':
			if (candidate.hasGeneratedStandardCV) return 'done';
			return getStepStatus('outreach', candidate) === 'done' ? 'active' : 'pending';
		case 'interview':
			if (candidate.hasInterviewNotes) return 'done';
			return getStepStatus('cv', candidate) === 'done' ? 'active' : 'pending';
		case 'recommend':
			if (candidate.recommended) return 'done';
			// Minimum requirement to recommend: the Standard CV is generated (the CV step is green).
			if (candidate.hasGeneratedStandardCV) return 'active';
			return 'pending';
		default:
			return 'pending';
	}
}

export function CandidatePipelineCard({ candidate, hiringRequestId, onOpenDetail, onRefresh }: CandidatePipelineCardProps) {
	const { toast } = useToast();
	const [expanded, setExpanded] = useState(false);
	const [activeTab, setActiveTab] = useState<string>('outreach');

	// Fetch vault contacts when expanded
	const { data: vaultResponse } = useQuery({
		queryKey: ['candidate-vault-detail', candidate.signalhireId],
		queryFn: () => sourcingApi.getCandidateVaultDetail(candidate.signalhireId),
		enabled: expanded && !!candidate.signalhireId
	});

	const candidatePhones = (vaultResponse?.data?.contacts || [])
		.filter((c: any) => c.type === 'phone')
		.map((c: any) => ({ value: c.value, subType: c.subType }));

	const candidateEmails = (vaultResponse?.data?.contacts || [])
		.filter((c: any) => c.type === 'email')
		.map((c: any) => ({ value: c.value, subType: c.subType }));

	// Recommend mutation (select for interview)
	const recommendMutation = useMutation({
		mutationFn: () => sourcingApi.selectForInterview(candidate.id),
		onSuccess: () => {
			onRefresh();
			toast({ title: 'Recommended', description: 'Candidate has been recommended to the hiring manager.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Cannot Recommend', description: err.message, variant: 'destructive' });
		}
	});

	// Unrecommend mutation
	const unrecommendMutation = useMutation({
		mutationFn: () => sourcingApi.unrecommendCandidate(candidate.id),
		onSuccess: () => {
			onRefresh();
			toast({ title: 'Unrecommended', description: 'Candidate recommendation has been reverted.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Withdraw mutation
	const withdrawMutation = useMutation({
		mutationFn: () => sourcingApi.withdrawCandidate(candidate.id),
		onSuccess: () => {
			onRefresh();
			toast({ title: 'Withdrawn', description: 'Candidate has been withdrawn from this hiring request.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Cancel stuck pending lookup (remove candidate from current pipeline)
	const cancelLookupMutation = useMutation({
		mutationFn: () => sourcingApi.removeCandidate(candidate.id),
		onSuccess: () => {
			onRefresh();
			setExpanded(false);
			toast({ title: 'Lookup Cancelled', description: 'Pending lookup has been removed from this hiring request.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Unsave candidate (put back — remove from this hiring request's saved list)
	const unsaveMutation = useMutation({
		mutationFn: () => sourcingApi.removeCandidate(candidate.id),
		onSuccess: () => {
			onRefresh();
			setExpanded(false);
			toast({ title: 'Removed', description: `${candidate.name} has been removed from this hiring request.` });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Hire state & mutation
	const [hireDialogOpen, setHireDialogOpen] = useState(false);
	const [hireStartDate, setHireStartDate] = useState('');

	// Reject dialog state
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [rejectReason, setRejectReason] = useState('');
	const [rejectedByValue, setRejectedByValue] = useState<'us' | 'company' | 'candidate'>('us');

	const hireMutation = useMutation({
		mutationFn: () => sourcingApi.hireCandidate(candidate.id, hireStartDate),
		onSuccess: () => {
			onRefresh();
			setHireDialogOpen(false);
			setHireStartDate('');
			toast({ title: 'Hired!', description: `${candidate.name} has been marked as hired.` });
		},
		onError: (err: Error) => {
			toast({ title: 'Cannot Hire', description: err.message, variant: 'destructive' });
		}
	});

	// Unhire mutation (revert hired → recommended)
	const unhireMutation = useMutation({
		mutationFn: () => sourcingApi.unhireCandidate(candidate.id),
		onSuccess: () => {
			onRefresh();
			toast({ title: 'Reverted', description: `${candidate.name} has been reverted to Recommended.` });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Reject / revert-rejection mutations
	const rejectMutation = useMutation({
		mutationFn: () => sourcingApi.rejectCandidate(candidate.id, { reason: rejectReason.trim(), rejectedBy: rejectedByValue }),
		onSuccess: () => {
			onRefresh();
			setRejectDialogOpen(false);
			setRejectReason('');
			setRejectedByValue('us');
			toast({ title: 'Rejected', description: `${candidate.name} has been marked as rejected.` });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	const unrejectMutation = useMutation({
		mutationFn: () => sourcingApi.unrejectCandidate(candidate.id),
		onSuccess: () => {
			onRefresh();
			toast({ title: 'Reverted', description: `${candidate.name}'s rejection has been reverted.` });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	const canRecommend = getStepStatus('recommend', candidate) === 'active';
	const isRecommended = candidate.recommended;
	const isHired = candidate.status === 'hired';
	const isWithdrawn = candidate.status === 'withdrawn';
	const isPendingLookup = candidate.name === 'Pending lookup...';
	const isManualCandidate = candidate.signalhireId?.startsWith('manual-');
	const isRejected = candidate.status === 'rejected';

	// Reusable Reject action — offered across the non-terminal states.
	const rejectButton = (
		<Button
			variant="ghost"
			size="sm"
			className="text-red-600"
			onClick={() => setRejectDialogOpen(true)}
			disabled={rejectMutation.isPending}
			title="Reject candidate"
		>
			<UserX className="h-4 w-4" />
		</Button>
	);

	return (
		<Card className={`overflow-hidden ${isWithdrawn ? 'opacity-60' : ''}`}>
			{/* Header row */}
			<div
				className="flex items-center justify-between p-4"
			>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2 flex-wrap">
						<span className={`font-medium truncate ${isPendingLookup ? 'italic text-muted-foreground' : ''}`}>
							{isPendingLookup ? 'Looking up profile...' : candidate.name}
						</span>
						{isPendingLookup && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary" />}
						{isManualCandidate && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-600">Manual</Badge>}
						{isHired && <Badge variant="success" className="text-xs">Hired</Badge>}
						{isWithdrawn && <Badge variant="outline" className="text-xs text-red-600 border-red-300">Withdrawn</Badge>}
					</div>
					<div className="text-sm text-muted-foreground truncate">
						{candidate.currentTitle} at {candidate.currentCompany}
					</div>
					{isRejected && candidate.rejectedReason && (
						<div className="text-xs text-red-600 truncate mt-0.5" title={candidate.rejectedReason}>
							Rejected{candidate.rejectedBy ? ` by ${REJECTED_BY_LABELS[candidate.rejectedBy]}` : ''}{candidate.rejectedByUser ? ` (${candidate.rejectedByUser})` : ''}: {candidate.rejectedReason}
						</div>
					)}
				</div>

				{/* Pipeline step indicators */}
				<div className="flex items-center gap-1 mx-4">
					{PIPELINE_STEPS.map((step, i) => {
						const status = getStepStatus(step.key, candidate);
						const Icon = step.icon;
						return (
							<div key={step.key} className="flex items-center">
								<div
									className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
										status === 'done'
											? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
											: status === 'active'
												? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
												: 'bg-muted text-muted-foreground'
									}`}
									title={step.label}
								>
									{status === 'done' ? (
										<CheckCircle2 className="h-3 w-3" />
									) : (
										<Icon className="h-3 w-3" />
									)}
									<span className="hidden sm:inline">{step.label}</span>
								</div>
								{i < PIPELINE_STEPS.length - 1 && (
									<div className={`w-3 h-px mx-0.5 ${status === 'done' ? 'bg-green-400' : 'bg-muted-foreground/30'}`} />
								)}
							</div>
						);
					})}
				</div>

				{/* Right-side actions */}
				<div className="flex items-center gap-2">
					{candidate.linkedinUrl && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => window.open(candidate.linkedinUrl, '_blank', 'noopener,noreferrer')}
						>
							<ExternalLink className="h-4 w-4" />
						</Button>
					)}
					{/* Status-based action buttons */}
					{isHired ? (
						<>
							<Badge variant="success">Hired</Badge>
							<Button
								variant="ghost"
								size="sm"
								className="text-orange-600"
								onClick={() => {
									if (window.confirm(`Revert ${candidate.name} from Hired back to Recommended?`)) {
										unhireMutation.mutate();
									}
								}}
								disabled={unhireMutation.isPending}
								title="Revert to Recommended"
							>
								<Undo2 className="h-4 w-4" />
							</Button>
						</>
					) : isRejected ? (
						<>
							<Badge variant="outline" className="text-red-600">
								Rejected{candidate.rejectedBy ? ` · by ${REJECTED_BY_LABELS[candidate.rejectedBy]}` : ''}
							</Badge>
							<Button
								variant="ghost"
								size="sm"
								className="text-orange-600"
								onClick={() => unrejectMutation.mutate()}
								disabled={unrejectMutation.isPending}
								title="Revert rejection"
							>
								<Undo2 className="h-4 w-4" />
							</Button>
						</>
					) : isWithdrawn ? (
						<Badge variant="outline" className="text-red-600">Withdrawn</Badge>
					) : isRecommended ? (
						<>
							<Badge variant="success">Recommended</Badge>
							<Button
								variant="ghost"
								size="sm"
								className="text-orange-600"
								onClick={() => unrecommendMutation.mutate()}
								disabled={unrecommendMutation.isPending}
								title="Unrecommend"
							>
								<Undo2 className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								className="text-red-600"
								onClick={() => {
									if (window.confirm(`Withdraw ${candidate.name} from this hiring request?`)) {
										withdrawMutation.mutate();
									}
								}}
								disabled={withdrawMutation.isPending}
								title="Withdraw"
							>
								<Ban className="h-4 w-4" />
							</Button>
							<Button
								size="sm"
								className="bg-green-600 hover:bg-green-700 text-white"
								onClick={() => setHireDialogOpen(true)}
							>
								<UserCheck className="mr-1 h-4 w-4" />
								Hire
							</Button>
							{rejectButton}
						</>
					) : canRecommend ? (
						<>
							<Button
								variant="ghost"
								size="sm"
								className="text-red-600"
								onClick={() => {
									if (window.confirm(`Remove ${candidate.name} from this hiring request?`)) {
										unsaveMutation.mutate();
									}
								}}
								disabled={unsaveMutation.isPending}
								title="Unsave (put back)"
							>
								<Undo2 className="h-4 w-4" />
							</Button>
							<Button
								size="sm"
								onClick={() => recommendMutation.mutate()}
								disabled={recommendMutation.isPending}
							>
								{recommendMutation.isPending ? 'Processing...' : 'Recommend'}
							</Button>
							{rejectButton}
						</>
					) : (
						<>
							{isPendingLookup ? (
								<Button
									variant="ghost"
									size="sm"
									className="text-red-600"
									onClick={() => {
										if (window.confirm('Cancel this failed lookup and remove it from pipeline?')) {
											cancelLookupMutation.mutate();
										}
									}}
									disabled={cancelLookupMutation.isPending}
									title="Cancel Lookup"
								>
									<XCircle className="h-4 w-4" />
								</Button>
							) : (
								<Button
									variant="ghost"
									size="sm"
									className="text-red-600"
									onClick={() => {
										if (window.confirm(`Remove ${candidate.name} from this hiring request?`)) {
											unsaveMutation.mutate();
										}
									}}
									disabled={unsaveMutation.isPending}
									title="Unsave (put back)"
								>
									<Undo2 className="h-4 w-4" />
								</Button>
							)}
							{!isPendingLookup && rejectButton}
							<Badge variant="outline" className="text-xs">Pipeline Incomplete</Badge>
						</>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => onOpenDetail(candidate)}
						className="gap-1.5"
					>
						<Eye className="h-4 w-4" />
						Preview
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="ml-1"
						onClick={() => setExpanded(!expanded)}
					>
						{expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
					</Button>
				</div>
			</div>

			{/* Hire Confirmation Dialog */}
			<Dialog open={hireDialogOpen} onOpenChange={setHireDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirm Hire</DialogTitle>
						<DialogDescription>
							Mark <strong>{candidate.name}</strong> as hired for this position.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>Start Date</Label>
							<Input
								type="date"
								value={hireStartDate}
								onChange={(e) => setHireStartDate(e.target.value)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setHireDialogOpen(false)}>Cancel</Button>
						<Button
							onClick={() => hireMutation.mutate()}
							disabled={!hireStartDate || hireMutation.isPending}
							className="bg-green-600 hover:bg-green-700"
						>
							{hireMutation.isPending ? 'Processing...' : 'Confirm Hire'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Reject Dialog */}
			<Dialog open={rejectDialogOpen} onOpenChange={(open) => { if (!open) setRejectDialogOpen(false); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Reject Candidate</DialogTitle>
						<DialogDescription>
							Mark <strong>{candidate.name}</strong> as rejected. Record who rejected and why.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>Rejected by</Label>
							<Select value={rejectedByValue} onValueChange={(v) => setRejectedByValue(v as 'us' | 'company' | 'candidate')}>
								<SelectTrigger>
									<SelectValue placeholder="Select who rejected" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="us">Us</SelectItem>
									<SelectItem value="company">Company</SelectItem>
									<SelectItem value="candidate">Candidate</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="grid gap-2">
							<Label>Reason</Label>
							<Textarea
								value={rejectReason}
								onChange={(e) => setRejectReason(e.target.value)}
								placeholder="Why is this candidate being rejected?"
								rows={3}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
						<Button
							variant="destructive"
							onClick={() => rejectMutation.mutate()}
							disabled={!rejectReason.trim() || rejectMutation.isPending}
						>
							{rejectMutation.isPending ? 'Processing...' : 'Confirm Reject'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Expanded pipeline panels */}
			{expanded && (
				<CardContent className="border-t pt-4">
					<Tabs value={activeTab} onValueChange={setActiveTab}>
						<TabsList className="w-full justify-start">
							<TabsTrigger value="outreach">Outreach</TabsTrigger>
							<TabsTrigger value="questions">Interview Questions</TabsTrigger>
							<TabsTrigger value="interview">Interview Notes</TabsTrigger>
							<TabsTrigger value="cv">CV Generation</TabsTrigger>
						</TabsList>

						<TabsContent value="outreach">
							<OutreachPanel
								sourcedCandidateId={candidate.id}
								hiringRequestId={hiringRequestId}
								candidatePhones={candidatePhones}
								candidateEmails={candidateEmails}
								onStatusChange={onRefresh}
							/>
						</TabsContent>

						<TabsContent value="questions">
							<InterviewQuestionsPanel
								sourcedCandidateId={candidate.id}
								hiringRequestId={hiringRequestId}
							/>
						</TabsContent>

						<TabsContent value="interview">
							<InterviewNotesPanel
								sourcedCandidateId={candidate.id}
								hiringRequestId={hiringRequestId}
								onNotesChange={onRefresh}
							/>
						</TabsContent>

						<TabsContent value="cv">
							<CVGenerationPanel
								sourcedCandidateId={candidate.id}
								hiringRequestId={hiringRequestId}
								hasCvFile={!!candidate.cvFileUrl}
								hasSignalHireData={!!candidate.signalhireId && !candidate.signalhireId.startsWith('manual-')}
								cvOriginalName={candidate.cvOriginalName}
								onCvUploaded={onRefresh}
							/>
						</TabsContent>
					</Tabs>
				</CardContent>
			)}
		</Card>
	);
}
